import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Panel,
  MarkerType,
  useReactFlow,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Eye,
  Target,
  Shield,
  Zap,
  Clock,
  AlertTriangle,
  Wrench,
  Search,
  Lock,
  Database,
  X,
  Info,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit3,
  Trash2,
  Save,
  Download,
  Upload,
  Settings,
  Link,
  Copy,
  RotateCcw,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Grid,
  Layers,
  Filter,
  RefreshCw
} from 'lucide-react';
import { nodeTypes } from './PhaseNodes';
import { processPhaseData } from '../../utils/phaseDataProcessor';
import { optimizeReactFlowData, usePerformanceMonitor } from '../../utils/performance';

// Using enhanced node components from PhaseNodes.js

/**
 * PhaseVisualization - Component for visualizing individual EBIOS RM phases
 * Implements FR-024: Phase-specific visualization with detailed React Flow diagrams
 */
const PhaseVisualization = ({
  scenario,
  selectedPhase = null,
  onPhaseChange,
  onClose,
  isModal = true,
  isInline = false
}) => {
  // Performance monitoring
  const { logPerformance } = usePerformanceMonitor('PhaseVisualization');

  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  // Enhanced state for practical tools
  const [isEditing, setIsEditing] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [showNodePanel, setShowNodePanel] = useState(false);
  const [showConnectionPanel, setShowConnectionPanel] = useState(false);
  const [selectedTool, setSelectedTool] = useState(null);
  const [nodeToEdit, setNodeToEdit] = useState(null);
  const [connectionMode, setConnectionMode] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [showGrid, setShowGrid] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Save functionality state
  const [isSaving, setIsSaving] = useState(false);
  const [isModified, setIsModified] = useState(false);

  // Viewport reference for proper modal positioning
  const reactFlowWrapper = useRef(null);
  const [viewportDimensions, setViewportDimensions] = useState({ width: 0, height: 0 });

  // Update viewport dimensions
  useEffect(() => {
    const updateDimensions = () => {
      if (reactFlowWrapper.current) {
        const rect = reactFlowWrapper.current.getBoundingClientRect();
        setViewportDimensions({ width: rect.width, height: rect.height });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);



  // Extract phases from scenario
  const phases = useMemo(() => {
    if (!scenario?.operationalSteps) return [];
    return scenario.operationalSteps.map(step => step.phase).filter(Boolean);
  }, [scenario]);

  const currentPhaseIndex = selectedPhase ? phases.indexOf(selectedPhase) : 0;
  const currentPhase = phases[currentPhaseIndex] || phases[0];

  // Get current phase step
  const currentStep = useMemo(() => {
    if (!scenario?.operationalSteps || !currentPhase) return null;
    return scenario.operationalSteps.find(step => step.phase === currentPhase);
  }, [scenario, currentPhase]);

  // Generate nodes and edges using enhanced data processor with optimization
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    if (!scenario) return { nodes: [], edges: [] };

    const startTime = performance.now();

    // Use the enhanced data processor
    const processedData = processPhaseData(scenario, currentPhase);

    // Optimize React Flow data for performance
    const optimizedData = optimizeReactFlowData(
      processedData.nodes || [],
      processedData.edges || [],
      {
        maxNodes: 50,
        maxEdges: 100,
        enableClustering: true,
        clusterThreshold: 25
      }
    );

    const endTime = performance.now();
    logPerformance('Phase data processing and optimization', endTime - startTime);

    if (optimizedData.optimizationApplied.nodeLimit || optimizedData.optimizationApplied.edgeLimit) {
      console.warn('[PhaseVisualization] Performance optimizations applied:', optimizedData.optimizationApplied);
    }

    return {
      nodes: optimizedData.nodes,
      edges: optimizedData.edges
    };
  }, [scenario, currentPhase, logPerformance]);

  const [nodes, setNodes, originalOnNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, originalOnEdgesChange] = useEdgesState(initialEdges);

  // Wrap the change handlers to track modifications
  const onNodesChange = useCallback(
    (changes) => {
      originalOnNodesChange(changes);
      setIsModified(true);
    },
    [originalOnNodesChange]
  );

  const onEdgesChange = useCallback(
    (changes) => {
      originalOnEdgesChange(changes);
      setIsModified(true);
    },
    [originalOnEdgesChange]
  );

  // Debug scenario data and edges
  console.log('=== PHASE VISUALIZATION DEBUG ===');
  console.log('Scenario:', scenario);
  console.log('Current Phase:', currentPhase);
  console.log('Current Step:', currentStep);
  console.log('Initial Nodes:', initialNodes);
  console.log('Initial Edges:', initialEdges);
  console.log('Current edges:', edges);
  console.log('================================');

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((_event, node) => {
    setSelectedNodeId(node.id);
    setShowDetails(true);
    if (connectionMode) {
      // Handle connection mode logic
      console.log('Connection mode - selected node:', node.id);
    }
  }, [connectionMode]);

  // Enhanced tool functions
  const addNewNode = useCallback((type = 'technique') => {
    // Get current viewport center for better node placement
    const viewportCenter = {
      x: viewportDimensions.width ? viewportDimensions.width / 2 : 200,
      y: viewportDimensions.height ? viewportDimensions.height / 2 : 200
    };

    // Add some randomness around the center
    const randomOffset = {
      x: (Math.random() - 0.5) * 200, // ±100px from center
      y: (Math.random() - 0.5) * 200
    };

    const newNode = {
      id: `${type}-${Date.now()}`,
      type: type,
      position: {
        x: Math.max(50, viewportCenter.x + randomOffset.x),
        y: Math.max(50, viewportCenter.y + randomOffset.y)
      },
      data: {
        label: `New ${type}`,
        phase: currentPhase,
        technique: `New ${type} technique`,
        description: 'Click to edit description',
        difficulty: 'Moyen',
        duration: '1 heure',
        tools: [],
        indicators: [],
        techniques: []
      }
    };
    setNodes(nds => [...nds, newNode]);

    // Auto-create intelligent connections
    setTimeout(() => createIntelligentConnections(newNode), 100);
  }, [currentPhase, setNodes, viewportDimensions]);

  const deleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, [setNodes, setEdges]);

  const duplicateNode = useCallback((nodeId) => {
    const nodeToDuplicate = nodes.find(n => n.id === nodeId);
    if (nodeToDuplicate) {
      const newNode = {
        ...nodeToDuplicate,
        id: `${nodeToDuplicate.id}-copy-${Date.now()}`,
        position: {
          x: nodeToDuplicate.position.x + 50,
          y: nodeToDuplicate.position.y + 50
        },
        data: {
          ...nodeToDuplicate.data,
          label: `${nodeToDuplicate.data.label} (Copy)`
        }
      };
      setNodes(nds => [...nds, newNode]);
    }
  }, [nodes, setNodes]);

  const editNode = useCallback((nodeId) => {
    const nodeToEdit = nodes.find(n => n.id === nodeId);
    if (nodeToEdit) {
      setNodeToEdit(nodeToEdit);
      setShowNodePanel(true);
    }
  }, [nodes]);

  const saveNodeChanges = useCallback((nodeId, newData) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n
    ));
    setShowNodePanel(false);
    setNodeToEdit(null);
  }, [setNodes]);

  const createConnection = useCallback((sourceId, targetId, label = '', relationship = 'uses') => {
    const newEdge = {
      id: `edge-${sourceId}-${targetId}`,
      source: sourceId,
      target: targetId,
      type: 'smoothstep',
      animated: true,
      label: label,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: getRelationshipColor(relationship)
      },
      style: {
        stroke: getRelationshipColor(relationship),
        strokeWidth: 2
      },
      data: {
        relationship: relationship,
        description: label
      }
    };
    setEdges(eds => addEdge(newEdge, eds));
  }, [setEdges]);

  // Get color based on relationship type
  const getRelationshipColor = (relationship) => {
    const colors = {
      'uses': '#3b82f6',      // Blue - technique uses tool
      'enables': '#10b981',   // Green - tool enables technique
      'detects': '#f59e0b',   // Orange - indicator detects technique
      'requires': '#ef4444',  // Red - technique requires tool
      'leads_to': '#8b5cf6',  // Purple - technique leads to another
      'triggers': '#ec4899'   // Pink - technique triggers indicator
    };
    return colors[relationship] || '#6b7280'; // Default gray
  };

  // Process AI-generated scenario and create intelligent connections
  const processAIScenarioWithConnections = useCallback((aiScenario) => {
    // First load the scenario data normally
    const processedNodes = [];
    const processedEdges = [];

    if (aiScenario.operationalSteps) {
      aiScenario.operationalSteps.forEach((step, index) => {
        const stepPhase = step.phase || phases[index] || 'CONNAITRE';

        // Create technique node
        const techniqueNode = {
          id: `technique-${stepPhase}-${index}`,
          type: 'technique',
          position: { x: 100 + (index * 300), y: 100 },
          data: {
            label: step.technique || `Technique ${index + 1}`,
            phase: stepPhase,
            technique: step.technique,
            description: step.description,
            difficulty: step.difficulty || 'Moyen',
            duration: step.duration || '1 heure',
            stepNumber: index + 1,
            tools: step.tools || [],
            indicators: step.indicators || [],
            techniques: [step.technique]
          }
        };
        processedNodes.push(techniqueNode);

        // Create tool nodes if specified
        if (step.tools && step.tools.length > 0) {
          step.tools.forEach((tool, toolIndex) => {
            const toolNode = {
              id: `tool-${stepPhase}-${index}-${toolIndex}`,
              type: 'tool',
              position: { x: 100 + (index * 300), y: 200 + (toolIndex * 80) },
              data: {
                label: tool,
                phase: stepPhase,
                technique: tool,
                description: `Tool: ${tool}`,
                difficulty: 'Moyen',
                duration: '30 min'
              }
            };
            processedNodes.push(toolNode);

            // Create connection: technique uses tool
            processedEdges.push({
              id: `edge-${techniqueNode.id}-${toolNode.id}`,
              source: techniqueNode.id,
              target: toolNode.id,
              type: 'smoothstep',
              animated: true,
              label: 'uses',
              style: { stroke: getRelationshipColor('uses'), strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: getRelationshipColor('uses') },
              data: { relationship: 'uses', description: 'uses' }
            });
          });
        }

        // Create indicator nodes if specified
        if (step.indicators && step.indicators.length > 0) {
          step.indicators.forEach((indicator, indicatorIndex) => {
            const indicatorNode = {
              id: `indicator-${stepPhase}-${index}-${indicatorIndex}`,
              type: 'indicator',
              position: { x: 300 + (index * 300), y: 200 + (indicatorIndex * 80) },
              data: {
                label: indicator,
                phase: stepPhase,
                technique: indicator,
                description: `Indicator: ${indicator}`,
                difficulty: 'Moyen',
                duration: 'Continuous'
              }
            };
            processedNodes.push(indicatorNode);

            // Create connection: technique triggers indicator
            processedEdges.push({
              id: `edge-${techniqueNode.id}-${indicatorNode.id}`,
              source: techniqueNode.id,
              target: indicatorNode.id,
              type: 'smoothstep',
              animated: true,
              label: 'triggers',
              style: { stroke: getRelationshipColor('triggers'), strokeWidth: 2 },
              markerEnd: { type: MarkerType.ArrowClosed, color: getRelationshipColor('triggers') },
              data: { relationship: 'triggers', description: 'triggers' }
            });
          });
        }
      });

      // Create sequential connections between techniques
      for (let i = 0; i < aiScenario.operationalSteps.length - 1; i++) {
        const currentTechniqueId = `technique-${aiScenario.operationalSteps[i].phase || phases[i]}-${i}`;
        const nextTechniqueId = `technique-${aiScenario.operationalSteps[i + 1].phase || phases[i + 1]}-${i + 1}`;

        processedEdges.push({
          id: `edge-${currentTechniqueId}-${nextTechniqueId}`,
          source: currentTechniqueId,
          target: nextTechniqueId,
          type: 'smoothstep',
          animated: true,
          label: 'leads to',
          style: { stroke: getRelationshipColor('leads_to'), strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: getRelationshipColor('leads_to') },
          data: { relationship: 'leads_to', description: 'leads to' }
        });
      }
    }

    // Update nodes and edges
    setNodes(processedNodes);
    setEdges(processedEdges);
  }, [setNodes, setEdges, phases]);

  // Create intelligent connections based on node relationships
  const createIntelligentConnections = useCallback((newNode) => {
    const currentNodes = nodes;
    const samePhaseNodes = currentNodes.filter(node =>
      node.data.phase === newNode.data.phase && node.id !== newNode.id
    );

    // Auto-connect based on node types and content
    samePhaseNodes.forEach(existingNode => {
      const connection = determineConnection(newNode, existingNode);
      if (connection) {
        createConnection(
          connection.sourceId,
          connection.targetId,
          connection.label,
          connection.relationship
        );
      }
    });
  }, [nodes, createConnection]);

  // Determine if two nodes should be connected and how
  const determineConnection = (node1, node2) => {
    const type1 = node1.type;
    const type2 = node2.type;

    // Technique -> Tool relationships
    if (type1 === 'technique' && type2 === 'tool') {
      return {
        sourceId: node1.id,
        targetId: node2.id,
        relationship: 'uses',
        label: 'uses'
      };
    }

    // Tool -> Technique relationships
    if (type1 === 'tool' && type2 === 'technique') {
      return {
        sourceId: node1.id,
        targetId: node2.id,
        relationship: 'enables',
        label: 'enables'
      };
    }

    // Technique -> Indicator relationships
    if (type1 === 'technique' && type2 === 'indicator') {
      return {
        sourceId: node1.id,
        targetId: node2.id,
        relationship: 'triggers',
        label: 'triggers'
      };
    }

    // Indicator -> Technique relationships (detection)
    if (type1 === 'indicator' && type2 === 'technique') {
      return {
        sourceId: node1.id,
        targetId: node2.id,
        relationship: 'detects',
        label: 'detects'
      };
    }

    // Sequential technique relationships (same phase)
    if (type1 === 'technique' && type2 === 'technique') {
      // Check if they should be connected sequentially
      const step1 = parseInt(node1.data.stepNumber) || 0;
      const step2 = parseInt(node2.data.stepNumber) || 0;

      if (step1 + 1 === step2) {
        return {
          sourceId: node1.id,
          targetId: node2.id,
          relationship: 'leads_to',
          label: 'leads to'
        };
      }
    }

    return null;
  };

  // Create intelligent connections for all existing nodes
  const createAllIntelligentConnections = useCallback(() => {
    const currentNodes = nodes;
    const phaseNodes = currentNodes.filter(node => node.data.phase === currentPhase);

    // Clear existing edges for this phase to avoid duplicates
    setEdges(eds => eds.filter(edge => {
      const sourceNode = currentNodes.find(n => n.id === edge.source);
      const targetNode = currentNodes.find(n => n.id === edge.target);
      return !(sourceNode?.data.phase === currentPhase && targetNode?.data.phase === currentPhase);
    }));

    // Create connections between all relevant node pairs
    phaseNodes.forEach(node1 => {
      phaseNodes.forEach(node2 => {
        if (node1.id !== node2.id) {
          const connection = determineConnection(node1, node2);
          if (connection) {
            createConnection(
              connection.sourceId,
              connection.targetId,
              connection.label,
              connection.relationship
            );
          }
        }
      });
    });
  }, [nodes, currentPhase, setEdges, createConnection]);

  // Create demo scenario for testing
  const createDemoScenario = useCallback(() => {
    console.log('Creating demo scenario...');

    // Create demo nodes and edges directly
    const demoNodes = [
      // CONNAITRE Phase
      {
        id: 'technique-connaitre-0',
        type: 'technique',
        position: { x: 100, y: 100 },
        data: {
          label: 'Network Reconnaissance',
          phase: 'CONNAITRE',
          technique: 'Network Reconnaissance',
          description: 'Perform network scanning to identify targets',
          difficulty: 'Faible',
          duration: '2 heures',
          stepNumber: 1,
          tools: ['Nmap', 'Masscan'],
          indicators: ['Port scan traffic', 'DNS queries']
        }
      },
      {
        id: 'tool-connaitre-0-0',
        type: 'tool',
        position: { x: 100, y: 200 },
        data: {
          label: 'Nmap',
          phase: 'CONNAITRE',
          technique: 'Nmap',
          description: 'Tool: Nmap',
          difficulty: 'Moyen',
          duration: '30 min'
        }
      },
      {
        id: 'tool-connaitre-0-1',
        type: 'tool',
        position: { x: 100, y: 280 },
        data: {
          label: 'Masscan',
          phase: 'CONNAITRE',
          technique: 'Masscan',
          description: 'Tool: Masscan',
          difficulty: 'Moyen',
          duration: '30 min'
        }
      },
      {
        id: 'indicator-connaitre-0-0',
        type: 'indicator',
        position: { x: 300, y: 200 },
        data: {
          label: 'Port scan traffic',
          phase: 'CONNAITRE',
          technique: 'Port scan traffic',
          description: 'Indicator: Port scan traffic',
          difficulty: 'Moyen',
          duration: 'Continuous'
        }
      },
      // RENTRER Phase
      {
        id: 'technique-rentrer-1',
        type: 'technique',
        position: { x: 500, y: 100 },
        data: {
          label: 'Phishing Attack',
          phase: 'RENTRER',
          technique: 'Phishing Attack',
          description: 'Send targeted phishing emails to gain initial access',
          difficulty: 'Moyen',
          duration: '4 heures',
          stepNumber: 2,
          tools: ['Gophish'],
          indicators: ['Suspicious emails']
        }
      },
      {
        id: 'tool-rentrer-1-0',
        type: 'tool',
        position: { x: 500, y: 200 },
        data: {
          label: 'Gophish',
          phase: 'RENTRER',
          technique: 'Gophish',
          description: 'Tool: Gophish',
          difficulty: 'Moyen',
          duration: '30 min'
        }
      }
    ];

    const demoEdges = [
      // Technique uses tools
      {
        id: 'edge-technique-connaitre-0-tool-connaitre-0-0',
        source: 'technique-connaitre-0',
        target: 'tool-connaitre-0-0',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
        data: { relationship: 'uses', description: 'uses' }
      },
      {
        id: 'edge-technique-connaitre-0-tool-connaitre-0-1',
        source: 'technique-connaitre-0',
        target: 'tool-connaitre-0-1',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
        data: { relationship: 'uses', description: 'uses' }
      },
      // Technique triggers indicators
      {
        id: 'edge-technique-connaitre-0-indicator-connaitre-0-0',
        source: 'technique-connaitre-0',
        target: 'indicator-connaitre-0-0',
        type: 'smoothstep',
        animated: true,
        label: 'triggers',
        style: { stroke: '#ec4899', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899' },
        data: { relationship: 'triggers', description: 'triggers' }
      },
      // Sequential flow
      {
        id: 'edge-technique-connaitre-0-technique-rentrer-1',
        source: 'technique-connaitre-0',
        target: 'technique-rentrer-1',
        type: 'smoothstep',
        animated: true,
        label: 'leads to',
        style: { stroke: '#8b5cf6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' },
        data: { relationship: 'leads_to', description: 'leads to' }
      },
      // RENTRER phase connections
      {
        id: 'edge-technique-rentrer-1-tool-rentrer-1-0',
        source: 'technique-rentrer-1',
        target: 'tool-rentrer-1-0',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' },
        data: { relationship: 'uses', description: 'uses' }
      }
    ];

    console.log('Setting demo nodes:', demoNodes.length);
    console.log('Setting demo edges:', demoEdges.length);

    setNodes(demoNodes);
    setEdges(demoEdges);
  }, [setNodes, setEdges]);

  // Simple test with just 2 nodes and 1 connection
  const createSimpleTest = useCallback(() => {
    console.log('Creating simple test...');

    const testNodes = [
      {
        id: 'node1',
        type: 'technique',
        position: { x: 100, y: 100 },
        data: {
          label: 'Test Technique',
          phase: currentPhase,
          technique: 'Test Technique',
          description: 'A test technique node'
        }
      },
      {
        id: 'node2',
        type: 'tool',
        position: { x: 300, y: 100 },
        data: {
          label: 'Test Tool',
          phase: currentPhase,
          technique: 'Test Tool',
          description: 'A test tool node'
        }
      }
    ];

    const testEdges = [
      {
        id: 'edge-node1-node2',
        source: 'node1',
        target: 'node2',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
      }
    ];

    console.log('Setting test nodes:', testNodes);
    console.log('Setting test edges:', testEdges);

    setNodes(testNodes);
    setEdges(testEdges);
  }, [setNodes, setEdges, currentPhase]);

  // Test basic connection functionality
  const testBasicConnection = useCallback(() => {
    console.log('Testing basic connection...');
    console.log('Current nodes:', nodes);
    console.log('Current edges before:', edges);

    if (nodes.length >= 2) {
      const newEdge = {
        id: `test-edge-${Date.now()}`,
        source: nodes[0].id,
        target: nodes[1].id,
        type: 'smoothstep',
        animated: true,
        label: 'test connection',
        style: { stroke: '#ff0000', strokeWidth: 4 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ff0000' }
      };

      console.log('Adding edge:', newEdge);
      setEdges(eds => [...eds, newEdge]);
    } else {
      console.log('Need at least 2 nodes to create connection');
    }
  }, [nodes, edges, setEdges]);

  // Debug real scenario data
  const debugRealData = useCallback(() => {
    console.log('=== DEBUGGING REAL SCENARIO DATA ===');
    console.log('Scenario object:', scenario);
    console.log('Current phase:', currentPhase);
    console.log('Current step:', currentStep);

    if (scenario?.operationalSteps) {
      console.log('Operational steps:', scenario.operationalSteps);
      scenario.operationalSteps.forEach((step, index) => {
        console.log(`Step ${index + 1}:`, {
          phase: step.phase,
          technique: step.technique,
          tools: step.tools,
          indicators: step.indicators,
          description: step.description
        });
      });
    } else {
      console.log('❌ No operationalSteps found in scenario');
    }

    // Try to manually process the current scenario
    if (scenario && currentPhase) {
      console.log('Attempting to process scenario data...');
      const processedData = processPhaseData(scenario, currentPhase);
      console.log('Processed data:', processedData);

      if (processedData.edges.length > 0) {
        console.log('✅ Found edges in processed data:', processedData.edges);
        setNodes(processedData.nodes);
        setEdges(processedData.edges);
      } else {
        console.log('❌ No edges generated from processed data');
      }
    }
  }, [scenario, currentPhase, currentStep, setNodes, setEdges]);

  // Create logical attack flow demo
  const createLogicalFlowDemo = useCallback(() => {
    console.log('Creating logical attack flow demo...');

    // Define clean layout for demo
    const demoLayout = {
      threatSource: { x: 50, y: 300 },
      phase: { x: 450, y: 300 },
      technique: { x: 850, y: 300 },
      tools: { x: 1200, y: 150 },
      indicators: { x: 1550, y: 150 },
      asset: { x: 850, y: 500 },
      spacing: 120
    };

    const logicalNodes = [
      // 1. Threat Source (initiates attack)
      {
        id: 'threat-source',
        type: 'threatSource',
        position: demoLayout.threatSource,
        data: {
          label: 'Cybercriminel',
          threatType: 'cybercrime',
          capability: 'Intermédiaire',
          motivation: 'Profit financier',
          description: 'Groupe de cybercriminalité organisée'
        }
      },
      // 2. Phase (attack phase)
      {
        id: 'phase-main',
        type: 'phase',
        position: demoLayout.phase,
        data: {
          phase: 'CONNAITRE',
          technique: 'Reconnaissance',
          description: 'Phase de reconnaissance pour identifier les cibles',
          difficulty: 'Faible',
          duration: '2 heures'
        }
      },
      // 3. Technique (specific attack method)
      {
        id: 'technique-1',
        type: 'technique',
        position: demoLayout.technique,
        data: {
          label: 'Network Scanning',
          description: 'Scan des ports et services réseau',
          mitreId: 'T1046'
        }
      },
      // 4. Tools (used by technique) - vertically stacked
      {
        id: 'tool-1',
        type: 'tool',
        position: { x: demoLayout.tools.x, y: demoLayout.tools.y },
        data: {
          label: 'Nmap',
          description: 'Outil de scan réseau'
        }
      },
      {
        id: 'tool-2',
        type: 'tool',
        position: { x: demoLayout.tools.x, y: demoLayout.tools.y + demoLayout.spacing },
        data: {
          label: 'Masscan',
          description: 'Scanner de ports rapide'
        }
      },
      // 5. Indicators (generated by tools) - vertically stacked
      {
        id: 'indicator-1',
        type: 'indicator',
        position: { x: demoLayout.indicators.x, y: demoLayout.indicators.y },
        data: {
          label: 'Port scan traffic',
          description: 'Trafic de scan de ports détectable',
          severity: 'Medium'
        }
      },
      {
        id: 'indicator-2',
        type: 'indicator',
        position: { x: demoLayout.indicators.x, y: demoLayout.indicators.y + demoLayout.spacing },
        data: {
          label: 'DNS queries',
          description: 'Requêtes DNS suspectes',
          severity: 'Low'
        }
      },
      // 6. Asset (target)
      {
        id: 'asset-1',
        type: 'asset',
        position: demoLayout.asset,
        data: {
          label: 'Serveur Web',
          assetType: 'server',
          criticality: 'High'
        }
      }
    ];

    const logicalEdges = [
      // Attack flow: Threat → Phase → Technique → Tools → Indicators
      {
        id: 'edge-threat-phase',
        source: 'threat-source',
        target: 'phase-main',
        type: 'smoothstep',
        animated: true,
        label: 'initiates',
        style: { stroke: '#8b5cf6', strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#8b5cf6' }
      },
      {
        id: 'edge-phase-technique',
        source: 'phase-main',
        target: 'technique-1',
        type: 'smoothstep',
        animated: true,
        label: 'employs',
        style: { stroke: '#3b82f6', strokeWidth: 3 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
      },
      {
        id: 'edge-technique-tool1',
        source: 'technique-1',
        target: 'tool-1',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
      },
      {
        id: 'edge-technique-tool2',
        source: 'technique-1',
        target: 'tool-2',
        type: 'smoothstep',
        animated: true,
        label: 'uses',
        style: { stroke: '#3b82f6', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6' }
      },
      {
        id: 'edge-tool1-indicator1',
        source: 'tool-1',
        target: 'indicator-1',
        type: 'smoothstep',
        animated: true,
        label: 'generates',
        style: { stroke: '#ec4899', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899' }
      },
      {
        id: 'edge-tool2-indicator2',
        source: 'tool-2',
        target: 'indicator-2',
        type: 'smoothstep',
        animated: true,
        label: 'generates',
        style: { stroke: '#ec4899', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ec4899' }
      },
      {
        id: 'edge-technique-asset',
        source: 'technique-1',
        target: 'asset-1',
        type: 'smoothstep',
        animated: true,
        label: 'targets',
        style: { stroke: '#ef4444', strokeWidth: 2 },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#ef4444' }
      }
    ];

    console.log('Setting logical flow nodes:', logicalNodes.length);
    console.log('Setting logical flow edges:', logicalEdges.length);

    setNodes(logicalNodes);
    setEdges(logicalEdges);
  }, [setNodes, setEdges]);

  const filterNodes = useCallback((type) => {
    setFilterType(type);
    if (type === 'all') {
      // Show all nodes
      setNodes(nds => nds.map(n => ({ ...n, hidden: false })));
    } else {
      // Filter by type
      setNodes(nds => nds.map(n => ({
        ...n,
        hidden: n.type !== type && type !== 'all'
      })));
    }
  }, [setNodes]);

  const handlePhaseNavigation = (direction) => {
    const newIndex = direction === 'next' 
      ? Math.min(currentPhaseIndex + 1, phases.length - 1)
      : Math.max(currentPhaseIndex - 1, 0);
    
    const newPhase = phases[newIndex];
    if (onPhaseChange) {
      onPhaseChange(newPhase);
    }
  };

  const selectedNode = nodes.find(node => node.id === selectedNodeId);

  if (!scenario || !currentStep) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <Target className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Aucune donnée de phase disponible</p>
        </div>
      </div>
    );
  }

  const content = (
    <div className="h-full flex flex-col">
      {/* Header - only show when not inline */}
      {!isInline && (
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Visualisation Phase: {currentPhase}
          </h3>
          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
            {scenario.name}
          </span>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Phase navigation */}
          {phases.length > 1 && (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePhaseNavigation('prev')}
                disabled={currentPhaseIndex === 0}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="text-sm text-gray-600">
                {currentPhaseIndex + 1} / {phases.length}
              </span>
              <button
                onClick={() => handlePhaseNavigation('next')}
                disabled={currentPhaseIndex === phases.length - 1}
                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {isModal && onClose && (
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        </div>
      )}

      {/* Enhanced React Flow Visualization */}
      <div className="flex-1 relative" ref={reactFlowWrapper}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          attributionPosition="bottom-left"
        >
          <Controls />
          <MiniMap />
          <Background variant={showGrid ? "dots" : "lines"} gap={12} size={1} />

          {/* Enhanced Toolbar Panel */}
          {showToolbar && (
            <Panel position="top-center" className="bg-white rounded-lg shadow-lg border border-gray-200">
              <div className="flex items-center space-x-2 p-2">
                {/* Add Node Tools */}
                <div className="flex items-center space-x-1 border-r border-gray-200 pr-2">
                  <button
                    onClick={() => addNewNode('technique')}
                    className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Add Technique Node"
                  >
                    <Plus size={16} className="text-blue-600" />
                  </button>
                  <button
                    onClick={() => addNewNode('tool')}
                    className="p-2 hover:bg-green-50 rounded-lg transition-colors"
                    title="Add Tool Node"
                  >
                    <Wrench size={16} className="text-green-600" />
                  </button>
                  <button
                    onClick={() => addNewNode('indicator')}
                    className="p-2 hover:bg-yellow-50 rounded-lg transition-colors"
                    title="Add Indicator Node"
                  >
                    <AlertTriangle size={16} className="text-yellow-600" />
                  </button>

                  {/* Auto-Connect Button */}
                  <button
                    onClick={createAllIntelligentConnections}
                    className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
                    title="Auto-Connect Related Nodes"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-600">
                      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/>
                      <path d="m15 5 4 4"/>
                      <path d="M8 12l8-8"/>
                    </svg>
                  </button>
                </div>

                {/* Edit Tools */}
                <div className="flex items-center space-x-1 border-r border-gray-200 pr-2">
                  <button
                    onClick={() => selectedNodeId && editNode(selectedNodeId)}
                    disabled={!selectedNodeId}
                    className="p-2 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Edit Selected Node"
                  >
                    <Edit3 size={16} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => selectedNodeId && duplicateNode(selectedNodeId)}
                    disabled={!selectedNodeId}
                    className="p-2 hover:bg-gray-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Duplicate Selected Node"
                  >
                    <Copy size={16} className="text-gray-600" />
                  </button>
                  <button
                    onClick={() => selectedNodeId && deleteNode(selectedNodeId)}
                    disabled={!selectedNodeId}
                    className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                    title="Delete Selected Node"
                  >
                    <Trash2 size={16} className="text-red-600" />
                  </button>
                </div>

                {/* Connection Tools */}
                <div className="flex items-center space-x-1 border-r border-gray-200 pr-2">
                  <button
                    onClick={() => setConnectionMode(!connectionMode)}
                    className={`p-2 rounded-lg transition-colors ${
                      connectionMode ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-50 text-gray-600'
                    }`}
                    title="Connection Mode"
                  >
                    <Link size={16} />
                  </button>
                </div>

                {/* Filter Tools */}
                <div className="flex items-center space-x-1 border-r border-gray-200 pr-2">
                  <select
                    value={filterType}
                    onChange={(e) => filterNodes(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="all">All Nodes</option>
                    <option value="technique">Techniques</option>
                    <option value="tool">Tools</option>
                    <option value="indicator">Indicators</option>
                  </select>
                </div>

                {/* View Tools */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className={`p-2 rounded-lg transition-colors ${
                      showGrid ? 'bg-gray-100 text-gray-600' : 'hover:bg-gray-50 text-gray-600'
                    }`}
                    title="Toggle Grid"
                  >
                    <Grid size={16} />
                  </button>
                  <button
                    onClick={() => setShowToolbar(false)}
                    className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
                    title="Hide Toolbar"
                  >
                    <X size={16} className="text-gray-600" />
                  </button>
                </div>
              </div>
            </Panel>
          )}

          {/* Phase info panel */}
          <Panel position="top-left" className="bg-white p-4 rounded-lg shadow-lg border border-gray-200 max-w-sm">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${
                  currentStep.phase === 'CONNAITRE' ? 'bg-blue-500' :
                  currentStep.phase === 'RENTRER' ? 'bg-orange-500' :
                  currentStep.phase === 'TROUVER' ? 'bg-purple-500' :
                  currentStep.phase === 'EXPLOITER' ? 'bg-red-500' : 'bg-gray-500'
                }`}></div>
                <span className="font-semibold text-sm">{currentStep.phase}</span>
              </div>
              <div className="text-xs text-gray-600">
                <strong>Technique:</strong> {currentStep.technique}
              </div>
              {currentStep.difficulty && (
                <div className="text-xs text-gray-600">
                  <strong>Difficulté:</strong> {currentStep.difficulty}
                </div>
              )}
              {currentStep.stepProbability && (
                <div className="text-xs text-gray-600">
                  <strong>Probabilité:</strong> {currentStep.stepProbability}%
                </div>
              )}
            </div>
          </Panel>

          {/* Relationship Legend */}
          <Panel position="bottom-left" className="bg-white p-3 rounded-lg shadow-lg border">
            <h4 className="text-sm font-semibold mb-2 text-gray-700">Connection Types</h4>
            <div className="space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-blue-500"></div>
                <span>Uses</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-green-500"></div>
                <span>Enables</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-orange-500"></div>
                <span>Detects</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-red-500"></div>
                <span>Requires</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-purple-500"></div>
                <span>Leads to</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-0.5 bg-pink-500"></div>
                <span>Triggers</span>
              </div>
            </div>
          </Panel>
        </ReactFlow>

        {/* Enhanced Node Details Modal */}
        {showDetails && selectedNode && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h4 className="text-lg font-semibold">Node Details: {selectedNode.data.label || selectedNode.data.technique}</h4>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => editNode(selectedNode.id)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                    title="Edit Node"
                  >
                    <Edit3 size={16} />
                  </button>
                  <button
                    onClick={() => duplicateNode(selectedNode.id)}
                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                    title="Duplicate Node"
                  >
                    <Copy size={16} />
                  </button>
                  <button
                    onClick={() => deleteNode(selectedNode.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete Node"
                  >
                    <Trash2 size={16} />
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>

              <div className="p-4">
                <div className="space-y-4">
                  {/* Basic Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="font-medium text-gray-700">Type:</span>
                      <p className="text-sm text-gray-600 mt-1 capitalize">{selectedNode.type}</p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Phase:</span>
                      <p className="text-sm text-gray-600 mt-1">{selectedNode.data.phase}</p>
                    </div>
                  </div>

                  {/* Description */}
                  {selectedNode.data.description && (
                    <div>
                      <span className="font-medium text-gray-700">Description:</span>
                      <p className="text-sm text-gray-600 mt-1 leading-relaxed">{selectedNode.data.description}</p>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-4">
                    {selectedNode.data.duration && (
                      <div>
                        <span className="font-medium text-gray-700">Duration:</span>
                        <p className="text-sm text-gray-600 mt-1">{selectedNode.data.duration}</p>
                      </div>
                    )}
                    {selectedNode.data.difficulty && (
                      <div>
                        <span className="font-medium text-gray-700">Difficulty:</span>
                        <p className="text-sm text-gray-600 mt-1">{selectedNode.data.difficulty}</p>
                      </div>
                    )}
                  </div>

                  {/* Tools */}
                  {selectedNode.data.tools && selectedNode.data.tools.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Tools:</span>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {selectedNode.data.tools.map((tool, index) => (
                          <span key={index} className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                            {tool.name || tool}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Indicators */}
                  {selectedNode.data.indicators && selectedNode.data.indicators.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Indicators:</span>
                      <div className="space-y-1 mt-2">
                        {selectedNode.data.indicators.map((indicator, index) => (
                          <div key={index} className="bg-yellow-50 border border-yellow-200 px-2 py-1 rounded text-xs">
                            {indicator.name || indicator}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Techniques */}
                  {selectedNode.data.techniques && selectedNode.data.techniques.length > 0 && (
                    <div>
                      <span className="font-medium text-gray-700">Related Techniques:</span>
                      <div className="space-y-1 mt-2">
                        {selectedNode.data.techniques.map((technique, index) => (
                          <div key={index} className="bg-purple-50 border border-purple-200 px-2 py-1 rounded text-xs">
                            {technique.name || technique}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => addNewNode(selectedNode.type)}
                      className="bg-blue-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center"
                    >
                      <Plus size={14} className="mr-1" />
                      Add Similar
                    </button>
                    <button
                      onClick={() => setConnectionMode(true)}
                      className="bg-green-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-green-700 flex items-center"
                    >
                      <Link size={14} className="mr-1" />
                      Connect
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(selectedNode.data, null, 2));
                      }}
                      className="bg-gray-600 text-white px-3 py-2 rounded-lg text-sm hover:bg-gray-700 flex items-center"
                    >
                      <Copy size={14} className="mr-1" />
                      Copy Data
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Node Editing Panel */}
        {showNodePanel && nodeToEdit && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h4 className="text-lg font-semibold">Edit Node: {nodeToEdit.data.label}</h4>
                <button
                  onClick={() => setShowNodePanel(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="p-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name:</label>
                      <input
                        type="text"
                        defaultValue={nodeToEdit.data.label}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        onChange={(e) => setNodeToEdit({
                          ...nodeToEdit,
                          data: { ...nodeToEdit.data, label: e.target.value }
                        })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phase:</label>
                      <select
                        defaultValue={nodeToEdit.data.phase}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        onChange={(e) => setNodeToEdit({
                          ...nodeToEdit,
                          data: { ...nodeToEdit.data, phase: e.target.value }
                        })}
                      >
                        <option value="CONNAITRE">CONNAITRE</option>
                        <option value="RENTRER">RENTRER</option>
                        <option value="TROUVER">TROUVER</option>
                        <option value="EXPLOITER">EXPLOITER</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
                    <textarea
                      defaultValue={nodeToEdit.data.description}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-24"
                      onChange={(e) => setNodeToEdit({
                        ...nodeToEdit,
                        data: { ...nodeToEdit.data, description: e.target.value }
                      })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty:</label>
                      <select
                        defaultValue={nodeToEdit.data.difficulty}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        onChange={(e) => setNodeToEdit({
                          ...nodeToEdit,
                          data: { ...nodeToEdit.data, difficulty: e.target.value }
                        })}
                      >
                        <option value="Faible">Faible</option>
                        <option value="Moyen">Moyen</option>
                        <option value="Élevé">Élevé</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration:</label>
                      <input
                        type="text"
                        defaultValue={nodeToEdit.data.duration}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        placeholder="e.g., 2 heures, 1 jour"
                        onChange={(e) => setNodeToEdit({
                          ...nodeToEdit,
                          data: { ...nodeToEdit.data, duration: e.target.value }
                        })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => saveNodeChanges(nodeToEdit.id, nodeToEdit.data)}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 flex items-center"
                    >
                      <Save size={14} className="mr-1" />
                      Save Changes
                    </button>
                    <button
                      onClick={() => setShowNodePanel(false)}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setNodeToEdit({ ...nodeToEdit, data: { ...nodeToEdit.data, label: '', description: '', difficulty: 'Moyen', duration: '' }});
                      }}
                      className="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-yellow-600 flex items-center"
                    >
                      <RefreshCw size={14} className="mr-1" />
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full h-full max-w-6xl max-h-[90vh] m-4">
          {content}
        </div>
      </div>
    );
  }

  return <div className="h-full">{content}</div>;
};

export default PhaseVisualization;
